import { Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { styled, keyframes } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import AiLoader from './AiLoader';
import { AI_IMAGE_FILE } from './constants';
import getOverallSentimentConfig from './lib/getOverallSentimentConfig';
import { useAiSentiment } from './AiSentimentProvider';
import { useServiceModule } from '../../providers/ServiceModuleProvider';

const borderWidth = 1.5;

// Keyframes for continuous rotation
const slowRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const StyledAiButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '8px',
  border: `${borderWidth}px solid transparent`,
  borderColor: theme.palette.primary.main,
  transition: 'all 0.3s ease',
  backgroundClip: 'padding-box',
  lineHeight: 1.25,
  fontWeight: 400,

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    padding: `${borderWidth}px`,
    background:
      'linear-gradient(320deg, red, orange, yellow, green, blue, indigo, violet)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },

  '&:hover::before': {
    opacity: 1,
  },

  '&:hover': {
    color: theme.palette.tertiary.main,
    border: `${borderWidth}px solid transparent !important`,
  },

  '.MuiButton-startIcon': {
    transition: 'transform 0.5s ease',
  },

  '&:hover .MuiButton-startIcon': {
    animation: `${slowRotate} 5s linear infinite`,
  },
}));

function AiButton({ formData, userAccess }) {
  const { loadingAiAnalysis, aiAnalysis } = useAiSentiment();
  const { moduleSettings } = useServiceModule();
  const displayButton =
    (userAccess.isSA() || userAccess.isAdmin()) &&
    moduleSettings?.insightPurchased &&
    formData?.serviceJobStatus !== 'INCOMPLETE' &&
    aiAnalysis !== null;
  const [aiData, setAiData] = useState(null);

  const initialize = async () => {
    if (aiAnalysis?.ai?.sentiment) {
      const { text, Icon, color } = getOverallSentimentConfig({
        type: aiAnalysis?.ai?.sentiment,
      });
      setAiData({
        ...aiAnalysis?.ai,
        sentimentConfig: { text, Icon, color },
      });
    }
  };

  useEffect(() => {
    if (displayButton) {
      initialize(formData?.id);
    }
  }, [formData?.id]);

  if (displayButton) {
    if (loadingAiAnalysis) {
      return (
        <AiLoader size={30}>
          <FormattedMessage id="LOADING" defaultMessage="Loading" />
          ...
        </AiLoader>
      );
    }
    return (
      <StyledAiButton
        sx={{ fontSize: '21px' }}
        onClick={() => {
          const element = document.getElementById('SERVICEJOB_NOTES_HEADING');

          if (element) {
            const yOffset = -200; // stop 200px before the element
            const y =
              element.getBoundingClientRect().top +
              window.pageYOffset +
              yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }}
        startIcon={
          <img
            src={AI_IMAGE_FILE}
            alt="icon"
            style={{ width: 25, height: 25 }}
          />
        }
        {...(aiData?.sentimentConfig?.Icon && {
          endIcon: (
            <aiData.sentimentConfig.Icon
              color={aiData?.sentimentConfig?.color}
            />
          ),
        })}
        variant="outlined">
        Insights
      </StyledAiButton>
    );
  }

  return null;
}

export default AiButton;
